import { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { useStudio } from "../../studios/StudioContext";
import { useSession, useScope } from "../../auth/session";

// Per-studio API access. Every component that talks to the backend should use
// this hook so requests always target the active studio's server with the user's token.
export function useAdminApi() {
  const { apiBaseUrl } = useStudio();
  const session = useSession(apiBaseUrl);
  const token   = session?.token || "";
  return useMemo(() => ({
    API: apiBaseUrl,
    TOKEN: token,
    USER: session?.user || null,
    IS_OWNER: session?.user?.role === "owner",
    ADMIN_SECRET: token, // legacy name used across tabs; now the logged-in user's JWT
    AUTH: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
  }), [apiBaseUrl, token, session]);
}

export const PASS_TYPES = ["dropin", "pass4", "pass8", "member2x", "memberUnl", "private1", "private3", "private10"];
export const PASS_LABELS = {
  dropin:    "Drop-in",
  pass4:     "4-Class Pass",
  pass8:     "8-Class Pass",
  member2x:  "Membership — 2×/week",
  memberUnl: "Monthly",
  private1:  "1 Private Session",
  private3:  "3 Private Sessions",
  private10: "10 Private Sessions",
};

const CLASSES_FALLBACK = [];

export function useClasses() {
  const { apiBaseUrl } = useStudio();
  const session = useSession(apiBaseUrl);
  const scope   = useScope(apiBaseUrl);
  const [classes, setClasses] = useState(CLASSES_FALLBACK);
  useEffect(() => {
    fetch(`${apiBaseUrl}/schedule/classes`)
      .then(r => r.json())
      .then(data => {
        const loaded = (data.classes ?? []).map(c => {
          const days  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
          const day   = days[c.dayOfWeek] ?? "";
          const [h, m] = (c.time ?? "00:00").split(":").map(Number);
          const ampm  = h >= 12 ? "pm" : "am";
          const hour  = h % 12 || 12;
          const time  = `${hour}:${String(m).padStart(2,"0")}${ampm}`;
          const venue = c.venue === "venue_online" ? " (Online)" : "";
          return { id: c.id, label: `${day} ${time}${venue} — ${c.title}` };
        });
        if (loaded.length > 0) setClasses(loaded);
      })
      .catch(err => console.error("useClasses: failed to load class list", err));
  }, [apiBaseUrl]);
  const isOwner = session?.user?.role === "owner";
  return useMemo(() => {
    if (isOwner) return classes;
    if (!Array.isArray(scope)) return [];
    return classes.filter(c => scope.includes(c.id));
  }, [classes, scope, isOwner]);
}

export function toLocalDateStr(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

export function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(typeof dateStr === "string" ? dateStr + (dateStr.length === 10 ? "T12:00:00" : "") : dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function fmtMoney(n) {
  return "$" + (n ?? 0).toFixed(2);
}

export function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function exportXLSX(sheetName, rows, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}